import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, ArrowLeftIcon, ShieldCheckIcon } from 'lucide-react';
interface OTPVerificationProps {
  navigate: (screen: string, param?: string) => void;
  context?: string;
}
export function OTPVerification({
  navigate,
  context
}: OTPVerificationProps) {
  const [otp, setOtp] = useState(['', '', '', '']);
  const [timeLeft, setTimeLeft] = useState(300); // 5 minutes
  const [isLoading, setIsLoading] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  useEffect(() => {
    if (timeLeft <= 0) return;
    const timer = setInterval(() => setTimeLeft(prev => prev - 1), 1000);
    return () => clearInterval(timer);
  }, [timeLeft]);
  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };
  const handleChange = (index: number, value: string) => {
    if (isNaN(Number(value))) return;
    const newOtp = [...otp];
    // Take only the last character if multiple are pasted
    newOtp[index] = value.substring(value.length - 1);
    setOtp(newOtp);
    // Move to next input
    if (value !== '' && index < 3) {
      inputRefs.current[index + 1]?.focus();
    }
  };
  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && otp[index] === '' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };
  const handleVerify = (e: React.FormEvent) => {
    e.preventDefault();
    const otpString = otp.join('');
    if (otpString.length < 4) return;
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      if (context === 'reset') {
        navigate('reset-password');
      } else {
        navigate('kyc');
      }
    }, 1500);
  };
  const handleResend = () => {
    setTimeLeft(300);
    setOtp(['', '', '', '']);
    inputRefs.current[0]?.focus();
  };
  return <div className="flex-1 bg-slate-50 flex flex-col h-full relative overflow-hidden" data-id="element-2341">
      {/* Header Background */}
      <div className="absolute top-0 left-0 right-0 h-80 bg-[#FFD700] rounded-b-[3rem]" data-id="element-2342" />

      {/* Content */}
      <div className="flex-1 flex flex-col px-6 pt-12 pb-safe z-10 overflow-y-auto" data-id="element-2343">
        {/* Back Button */}
        <button onClick={() => navigate(context === 'reset' ? 'forgot-password' : 'register')} className="w-10 h-10 bg-white/40 backdrop-blur-md rounded-full flex items-center justify-center mb-6 shadow-sm" data-id="element-2344">
          <ArrowLeftIcon className="w-5 h-5 text-[#0D3B3B]" data-id="element-2345" />
        </button>

        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} className="flex flex-col items-center mb-10" data-id="element-2346">
          <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-lg mb-6" data-id="element-2347">
            <ShieldCheckIcon className="w-8 h-8 text-[#0D3B3B]" data-id="element-2348" />
          </div>
          <h1 className="text-3xl font-black text-[#0D3B3B] mb-2 text-center" data-id="element-2349">
            Verify Account
          </h1>
          <p className="text-[#0D3B3B]/70 text-sm text-center max-w-[260px] font-medium" data-id="element-2350">
            We've sent a 4-digit code to your email/phone. Enter it below to
            verify.
          </p>
        </motion.div>

        {/* Form */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 0.1
      }} className="bg-white rounded-[2rem] p-6 shadow-xl shadow-[#0D3B3B]/5 flex flex-col max-w-md w-full mx-auto" data-id="element-2351">
          <form onSubmit={handleVerify} className="flex flex-col" data-id="element-2352">
            <div className="flex justify-between gap-3 mb-8" data-id="element-2353">
              {otp.map((digit, index) => <input key={index} ref={el => inputRefs.current[index] = el} type="text" inputMode="numeric" value={digit} onChange={e => handleChange(index, e.target.value)} onKeyDown={e => handleKeyDown(index, e)} className="w-16 h-16 text-center text-2xl font-black text-[#0D3B3B] bg-gray-50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#2BA89D]/20 focus:border-[#2BA89D] transition-all" maxLength={1} required data-id="element-2354" />)}
            </div>

            <div className="flex items-center justify-between mb-8 px-1" data-id="element-2355">
              <p className="text-sm font-bold text-gray-500" data-id="element-2356">
                Code expires in:{' '}
                <span className="text-[#2BA89D]" data-id="element-2357">{formatTime(timeLeft)}</span>
              </p>
              <button type="button" onClick={handleResend} disabled={timeLeft > 0} className={`text-sm font-bold transition-colors ${timeLeft > 0 ? 'text-gray-300 cursor-not-allowed' : 'text-[#0D3B3B] hover:text-[#2BA89D]'}`} data-id="element-2358">
                Resend Code
              </button>
            </div>

            <button type="submit" disabled={isLoading || otp.join('').length < 4} className={`w-full py-4 rounded-2xl font-bold text-lg flex items-center justify-center gap-2 transition-all active:scale-95 ${isLoading || otp.join('').length < 4 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-[#0D3B3B] text-white hover:bg-[#092D2D] shadow-lg shadow-[#0D3B3B]/20'}`} data-id="element-2359">
              {isLoading ? <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-2360" /> : <>
                  Verify Code
                  <ArrowRightIcon className="w-5 h-5" data-id="element-2361" />
                </>}
            </button>
          </form>
        </motion.div>
      </div>
    </div>;
}